import { useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { PlanCard } from "@/components/PlanCard";
import { SubscribeDialog } from "@/components/SubscribeDialog";
import { Marquee } from "@/components/Marquee";
import { useQuote } from "@/components/QuoteDialog";
import { useCatalog } from "@/hooks/useCatalog";
import { SECTOR_CONTENT, SECTOR_LINKS } from "@/lib/sectors";

const SectorHero = ({ c, slug }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-[#141412] text-[#FAF8F5] grain" data-testid="sector-hero">
      <div className="relative mx-auto grid max-w-[1400px] items-end gap-12 px-5 pb-20 pt-36 sm:px-8 lg:grid-cols-12 lg:pt-44">
        <div className="lg:col-span-7">
          <p className="eyebrow text-[#C59B27]">{c.eyebrow}</p>
          <MaskedLines key={slug} onMount delay={0.1} lines={c.lines} italic={c.italic} className="mt-6 font-display text-6xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-7xl lg:text-[7rem]" testId="sector-heading" />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl text-base text-stone-300 sm:text-lg">{c.intro}</p>
            <a href="#plans" className="btn-gold mt-10" data-testid="sector-plans-link">
              View plans <ArrowDownRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
        <motion.div
          key={`img-${slug}`}
          initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.3, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
          className="arch h-[420px] overflow-hidden lg:col-span-5 lg:h-[600px]"
        >
          <motion.img style={{ y, scale: 1.2 }} src={c.image} alt={c.eyebrow} className="h-full w-full object-cover" />
        </motion.div>
      </div>
    </section>
  );
};

export default function Sector() {
  const { slug } = useParams();
  const c = SECTOR_CONTENT[slug];
  const { sectors, loading, failed } = useCatalog();
  const { openQuote } = useQuote();
  const [plan, setPlan] = useState(null);
  if (!c) return <Navigate to="/" replace />;
  const plans = Array.isArray(sectors?.[slug]?.plans) ? sectors[slug].plans : [];
  const others = SECTOR_LINKS.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`sector-page-${slug}`}>
      <SectorHero c={c} slug={slug} />
      <section className="bg-[#FAF8F5] py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-[28px] lg:sticky lg:top-28">
              <img src={c.detail} alt="Culifect kitchen" className="h-[340px] w-full object-cover lg:h-[560px]" />
            </div>
          </div>
          <div className="grid gap-x-10 sm:grid-cols-2 lg:col-span-7">
            {c.highlights.map((h, i) => (
              <Reveal key={h.t} delay={(i % 2) * 0.1} className="border-t border-stone-300 py-8">
                <span className="font-display text-4xl italic text-[#C59B27]">0{i + 1}</span>
                <h3 className="mt-4 font-display text-2xl font-semibold">{h.t}</h3>
                <p className="mt-3 text-stone-700">{h.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="plans" className="bg-[#EFEAE1] py-24 sm:py-32" data-testid="plans-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="mb-14 grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow text-[#9a7614]">Subscription plans</p>
              <MaskedLines key={`p-${slug}`} lines={["Subscribe today,", "or let's talk."]} italic={1} className="mt-4 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-6xl" />
            </div>
            <p className="text-stone-700 lg:col-span-5">
              Start a monthly plan online with secure card payment, or request a quote and we'll tailor the contract to your sites, headcount and menus.
            </p>
          </div>
          {loading && <p className="text-stone-600" data-testid="plans-loading">Loading plans…</p>}
          {failed && <p className="text-stone-700" data-testid="plans-error">Plans are unavailable right now. Please request a quote below.</p>}
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map((p, i) => (
              <PlanCard key={p.lookup_key} plan={p} i={i} onSubscribe={setPlan} onQuote={(pl) => openQuote(slug, pl.name)} />
            ))}
          </div>
          <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-[28px] border border-stone-400/50 p-8 sm:flex-row sm:items-center">
            <p className="font-display text-2xl">Multiple sites or something bespoke?</p>
            <button onClick={() => openQuote(slug)} className="btn-ink" data-testid="sector-bespoke-quote-button">
              Request a tailored quote <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <Marquee dark />
      <section className="bg-[#FAF8F5] py-20">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-5 sm:flex-row sm:px-8">
          {others.map((o) => (
            <Link key={o.slug} to={`/sectors/${o.slug}`} className="group flex flex-1 items-center justify-between border-b border-stone-300 py-6" data-testid={`sector-other-${o.slug}`}>
              <span className="font-display text-3xl sm:text-4xl">{o.label}</span>
              <ArrowUpRight className="h-6 w-6 transition-transform duration-500 group-hover:rotate-45" />
            </Link>
          ))}
        </div>
      </section>
      <SubscribeDialog plan={plan} sectorName={sectors?.[slug]?.name} onClose={() => setPlan(null)} />
    </div>
  );
}
